/**
 * SettingsPage.js - 系统设置页
 * 包含通用、运费、支付、邮件、通知模板等多个 Tab 设置面板
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Tabs,
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Upload,
  Modal,
  Table,
  Space,
  Tag,
  message,
  Typography,
  Divider,
  Spin,
  Row,
  Col,
} from 'antd';
import {
  SaveOutlined,
  UploadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { adminSettingApi } from '../../api/index';
import RichTextEditor from '../../components/RichTextEditor';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

export default function SettingsPage() {
  // 全局设置数据
  const [settings, setSettings] = useState({});
  const [loadingSettings, setLoadingSettings] = useState(true);

  // 各 Tab 的 saving 状态
  const [savingGeneral, setSavingGeneral] = useState(false);
  const [savingShipping, setSavingShipping] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingTelegram, setSavingTelegram] = useState(false);
  const [savingAnnouncement, setSavingAnnouncement] = useState(false);

  // 邮件模板列表
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  // 编辑邮件模板 Modal
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [savingTemplate, setSavingTemplate] = useState(false);

  // Logo 上传文件
  const [logoFileList, setLogoFileList] = useState([]);

  // 各 Tab Form 实例
  const [generalForm] = Form.useForm();
  const [shippingForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [emailForm] = Form.useForm();
  const [telegramForm] = Form.useForm();
  const [announcementForm] = Form.useForm();
  const [templateForm] = Form.useForm();

  // 拉取设置数据
  const fetchSettings = useCallback(async () => {
    setLoadingSettings(true);
    try {
      const data = await adminSettingApi.getSettings();
      setSettings(data || {});

      // 回填通用设置
      generalForm.setFieldsValue({
        storeName: data?.storeName,
        storeDescription: data?.description, // 后端字段名是 description
        contactEmail: data?.email,
        contactPhone: data?.phone,
        currency: data?.currency || 'USD',
        address: data?.address,
      });

      // 回填运费设置
      shippingForm.setFieldsValue({
        shippingBaseFee: data?.shippingBaseFee,
        freeShippingThreshold: data?.freeShippingThreshold,
      });

      // 回填支付设置
      paymentForm.setFieldsValue({
        nihaoPayToken: data?.nihaoPayToken,
        nihaoPayEnabled: data?.nihaoPayEnabled !== false,
      });

      // 回填邮件 SMTP 设置
      emailForm.setFieldsValue({
        smtpHost: data?.smtpHost,
        smtpPort: data?.smtpPort,
        smtpUsername: data?.smtpUsername,
        smtpPassword: data?.smtpPassword,
        smtpFrom: data?.smtpFrom,
        smtpFromName: data?.smtpFromName,
      });

      // 回填 Telegram 设置
      telegramForm.setFieldsValue({
        telegramBotToken: data?.telegramBotToken,
        telegramChatId: data?.telegramChatId,
        telegramEnabled: data?.telegramEnabled !== false,
      });

      // 回填公告设置
      announcementForm.setFieldsValue({
        announcement: data?.announcement,
        announcementEnabled: data?.announcementEnabled === true,
        maintenanceEnabled: data?.maintenanceEnabled === true,
        maintenanceMessage: data?.maintenanceMessage,
      });

      // 设置 favicon（使用 logoUrl）
      if (data?.logoUrl) {
        const link = document.querySelector("link[rel~='icon']");
        if (link) {
          link.href = data.logoUrl;
        } else {
          const newLink = document.createElement('link');
          newLink.rel = 'icon';
          newLink.href = data.logoUrl;
          document.head.appendChild(newLink);
        }
      }
    } catch (err) {
      message.error('加载设置失败：' + (err.message || '未知错误'));
    } finally {
      setLoadingSettings(false);
    }
  }, [generalForm, shippingForm, paymentForm, emailForm, telegramForm, announcementForm]);

  // 拉取邮件模板
  const fetchEmailTemplates = useCallback(async () => {
    setLoadingTemplates(true);
    try {
      const data = await adminSettingApi.getEmailTemplates();
      setEmailTemplates(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error('加载邮件模板失败：' + (err.message || '未知错误'));
    } finally {
      setLoadingTemplates(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchEmailTemplates();
  }, [fetchSettings, fetchEmailTemplates]);

  // 通用保存方法：将表单数据合并到设置并提交
  const saveSection = async (formInstance, setSaving, fields) => {
    try {
      const values = await formInstance.validateFields();
      setSaving(true);

      // 字段映射：前端字段名 → 后端字段名
      const payload = {};
      if (values.storeName !== undefined) payload.storeName = values.storeName;
      if (values.storeDescription !== undefined) payload.description = values.storeDescription;
      if (values.contactEmail !== undefined) payload.email = values.contactEmail;
      if (values.contactPhone !== undefined) payload.phone = values.contactPhone;
      if (values.currency !== undefined) payload.currency = values.currency;
      if (values.address !== undefined) payload.address = values.address;
      if (values.shippingBaseFee !== undefined) payload.shippingBaseFee = values.shippingBaseFee;
      if (values.freeShippingThreshold !== undefined) payload.freeShippingThreshold = values.freeShippingThreshold;
      if (values.nihaoPayToken !== undefined) payload.nihaoPayToken = values.nihaoPayToken;
      if (values.nihaoPayEnabled !== undefined) payload.nihaoPayEnabled = values.nihaoPayEnabled;
      if (values.smtpHost !== undefined) payload.smtpHost = values.smtpHost;
      if (values.smtpPort !== undefined) payload.smtpPort = values.smtpPort;
      if (values.smtpUsername !== undefined) payload.smtpUsername = values.smtpUsername;
      if (values.smtpPassword !== undefined) payload.smtpPassword = values.smtpPassword;
      if (values.smtpFrom !== undefined) payload.smtpFrom = values.smtpFrom;
      if (values.smtpFromName !== undefined) payload.smtpFromName = values.smtpFromName;
      if (values.telegramBotToken !== undefined) payload.telegramBotToken = values.telegramBotToken;
      if (values.telegramChatId !== undefined) payload.telegramChatId = values.telegramChatId;
      if (values.telegramEnabled !== undefined) payload.telegramEnabled = values.telegramEnabled;
      if (values.announcement !== undefined) payload.announcement = values.announcement;
      if (values.announcementEnabled !== undefined) payload.announcementEnabled = values.announcementEnabled;
      if (values.maintenanceEnabled !== undefined) payload.maintenanceEnabled = values.maintenanceEnabled;
      if (values.maintenanceMessage !== undefined) payload.maintenanceMessage = values.maintenanceMessage;

      await adminSettingApi.updateSettings(payload);
      message.success('设置已保存');
      fetchSettings();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  // 保存通用设置
  const handleSaveGeneral = async () => {
    // 先处理 Logo 上传（如果有新文件）
    if (logoFileList.length > 0) {
      try {
        const file = logoFileList[0].originFileObj || logoFileList[0];
        const formData = new FormData();
        formData.append('file', file);
        const result = await adminSettingApi.uploadLogo(formData);
        message.success('Logo 上传成功');
        setLogoFileList([]);
        // 将 Logo 设置为 favicon.ico
        const link = document.querySelector("link[rel~='icon']");
        if (link) {
          link.href = result;
        } else {
          const newLink = document.createElement('link');
          newLink.rel = 'icon';
          newLink.href = result;
          document.head.appendChild(newLink);
        }
      } catch (err) {
        message.warning('Logo 上传失败：' + (err.message || ''));
      }
    }
    await saveSection(generalForm, setSavingGeneral);
  };

  // 保存运费设置
  const handleSaveShipping = () =>
    saveSection(shippingForm, setSavingShipping);

  // 保存支付设置
  const handleSavePayment = () =>
    saveSection(paymentForm, setSavingPayment);

  // 保存邮件 SMTP 设置
  const handleSaveEmail = () =>
    saveSection(emailForm, setSavingEmail);

  // 保存 Telegram 设置
  const handleSaveTelegram = () =>
    saveSection(telegramForm, setSavingTelegram);

  // 保存公告设置
  const handleSaveAnnouncement = () =>
    saveSection(announcementForm, setSavingAnnouncement);

  // 测试 Telegram 通知
  const handleTestTelegram = async () => {
    try {
      const result = await adminSettingApi.testTelegram();
      if (result.success) {
        message.success(result.message || '测试消息已发送，请查看 Telegram');
      } else {
        message.error(result.message || '发送失败');
      }
    } catch (err) {
      message.error(err?.response?.data?.message || '发送失败');
    }
  };

  // 打开邮件模板编辑 Modal
  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    templateForm.setFieldsValue({
      label: template.label,
      subject: template.subject,
      body: template.bodyHtml,
      recipient: template.recipient || 'customer',
    });
    setTemplateModalVisible(true);
  };

  // 关闭邮件模板 Modal
  const handleTemplateModalClose = () => {
    setTemplateModalVisible(false);
    setEditingTemplate(null);
    templateForm.resetFields();
  };

  // 保存邮件模板
  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      setSavingTemplate(true);
      // 字段映射：body → bodyHtml
      const payload = {
        label: values.label,
        recipient: values.recipient,
        subject: values.subject,
        bodyHtml: values.body,
        enabled: values.enabled !== false,
      };
      await adminSettingApi.updateEmailTemplate(editingTemplate.id, payload);
      message.success('邮件模板已更新');
      handleTemplateModalClose();
      fetchEmailTemplates();
    } catch (err) {
      if (err?.errorFields) return;
      message.error('保存失败：' + (err.message || '未知错误'));
    } finally {
      setSavingTemplate(false);
    }
  };

  // 测试发送邮件
  const handleTestSend = (template) => {
    Modal.confirm({
      title: '测试发送邮件',
      content: (
        <div>
          <Input
            placeholder="请输入收件人邮箱"
            defaultValue={template.recipient === 'admin' ? 'admin@example.com' : ''}
            onChange={(e) => window.testEmailTo = e.target.value}
            autoFocus
          />
        </div>
      ),
      onOk: async () => {
        const to = window.testEmailTo;
        if (!to || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
          message.error('请输入有效的邮箱地址');
          return false;
        }
        try {
          await adminSettingApi.testSendEmail(template.id, { to });
          message.success('邮件已发送，请查收');
        } catch (err) {
          message.error('发送失败：' + (err.response?.data?.message || '未知错误'));
        }
        window.testEmailTo = null;
      },
    });
  };

  // Logo 上传前置检查
  const beforeLogoUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只支持图片格式');
      return Upload.LIST_IGNORE;
    }
    return false; // 阻止自动上传
  };

  // 邮件模板表格列
  const templateColumns = [
    {
      title: '模板名称',
      dataIndex: 'label',
      key: 'label',
      render: (label, record) => (
        <span style={{ fontWeight: 500 }}>
          {label || record.eventType || '未命名'}
        </span>
      ),
    },
    {
      title: '收件人',
      dataIndex: 'recipient',
      key: 'recipient',
      width: 100,
      render: (recipient) => {
        const config = {
          customer: { color: 'blue', text: '客户' },
          admin: { color: 'green', text: '管理员' },
        };
        const c = config[recipient] || { color: 'default', text: recipient || '-' };
        return <Tag color={c.color}>{c.text}</Tag>;
      },
    },
    {
      title: '邮件主题',
      dataIndex: 'subject',
      key: 'subject',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEditTemplate(record)}
          >
            编辑
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => handleTestSend(record)}
          >
            测试
          </Button>
        </Space>
      ),
    },
  ];

  // Tab 配置
  const tabItems = [
    {
      key: 'general',
      label: '通用设置',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={generalForm} layout="vertical">
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="storeName"
                  label="店铺名称"
                  rules={[{ required: true, message: '请输入店铺名称' }]}
                >
                  <Input placeholder="Your Store Name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="currency" label="默认货币">
                  <Select>
                    <Option value="USD">USD 美元</Option>
                    <Option value="CNY">CNY 人民币</Option>
                    <Option value="EUR">EUR 欧元</Option>
                    <Option value="GBP">GBP 英镑</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="storeDescription" label="店铺描述">
                  <TextArea rows={3} placeholder="简短介绍你的店铺" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contactEmail" label="联系邮箱">
                  <Input placeholder="contact@yourstore.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="contactPhone" label="联系电话">
                  <Input placeholder="+1 234 567 8900" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item name="address" label="店铺地址">
                  <TextArea rows={2} placeholder="完整地址" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            {/* Logo 上传 */}
            <Form.Item label="店铺 Logo">
              {settings?.logoUrl && (
                <div style={{ marginBottom: 12 }}>
                  <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    当前 Logo：
                  </Text>
                  <img
                    src={settings.logoUrl}
                    alt="store logo"
                    style={{ maxHeight: 60, maxWidth: 200, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4 }}
                  />
                </div>
              )}
              <Upload
                listType="picture"
                maxCount={1}
                beforeUpload={beforeLogoUpload}
                fileList={logoFileList}
                onChange={({ fileList }) => setLogoFileList(fileList)}
                accept="image/*"
              >
                <Button icon={<UploadOutlined />}>选择 Logo 图片</Button>
              </Upload>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                建议尺寸：200×60px，PNG/SVG 格式，保存时自动上传
              </Text>
            </Form.Item>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingGeneral}
              onClick={handleSaveGeneral}
            >
              保存通用设置
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'shipping',
      label: '运费设置',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={shippingForm} layout="vertical">
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item name="shippingBaseFee" label="基础运费 ($)">
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    placeholder="0.00"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="freeShippingThreshold"
                  label="免运费门槛 ($)"
                  extra="订单金额满此金额时免运费，0 表示不启用"
                >
                  <InputNumber
                    min={0}
                    precision={2}
                    prefix="$"
                    placeholder="0.00"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingShipping}
              onClick={handleSaveShipping}
            >
              保存运费设置
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'payment',
      label: '支付设置',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={paymentForm} layout="vertical">
            <Title level={5} style={{ marginBottom: 16 }}>
              NihaoPay 配置
            </Title>
            <Form.Item
              name="nihaoPayToken"
              label="NihaoPay API Token"
              extra="请从 NihaoPay 商户后台获取"
            >
              <Input.Password
                placeholder="输入 API Token"
                style={{ fontFamily: 'monospace' }}
              />
            </Form.Item>
            <Form.Item
              name="nihaoPayEnabled"
              label="启用 NihaoPay 支付"
              valuePropName="checked"
            >
              <Switch checkedChildren="已启用" unCheckedChildren="已禁用" />
            </Form.Item>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingPayment}
              onClick={handleSavePayment}
            >
              保存支付设置
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'email',
      label: '邮件设置',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={emailForm} layout="vertical">
            <Title level={5} style={{ marginBottom: 16 }}>
              SMTP 配置
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={16}>
                <Form.Item name="smtpHost" label="SMTP 服务器地址">
                  <Input placeholder="smtp.gmail.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item name="smtpPort" label="端口">
                  <InputNumber
                    min={1}
                    max={65535}
                    placeholder="587"
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="smtpUsername" label="SMTP 用户名">
                  <Input placeholder="your@email.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="smtpPassword" label="SMTP 密码">
                  <Input.Password placeholder="SMTP 密码或应用专用密码" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="smtpFrom"
                  label="发件人地址"
                  extra="显示在邮件发件人栏的邮箱地址"
                >
                  <Input placeholder="noreply@yourstore.com" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="smtpFromName"
                  label="发件人名称"
                  extra="显示在邮件发件人栏的名称，如 'YK2 商店'"
                >
                  <Input placeholder="YK2 商店" />
                </Form.Item>
              </Col>
            </Row>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingEmail}
              onClick={handleSaveEmail}
            >
              保存邮件设置
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'telegram',
      label: 'Telegram 通知',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={telegramForm} layout="vertical">
            <Title level={5} style={{ marginBottom: 16 }}>
              Telegram Bot 配置
            </Title>
            <Row gutter={[16, 0]}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="telegramBotToken"
                  label="Bot Token"
                  extra="从 Telegram BotFather 获取的 API Token"
                >
                  <Input.Password
                    placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                    style={{ fontFamily: 'monospace' }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  name="telegramChatId"
                  label="Chat ID"
                  extra="接收通知的聊天 ID（可以是个人或群组）"
                >
                  <Input placeholder="123456789 或 -100123456789" />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name="telegramEnabled"
                  label="启用 Telegram 通知"
                  valuePropName="checked"
                  extra="开启后，新订单创建时会发送通知到 Telegram"
                >
                  <Switch checkedChildren="已启用" unCheckedChildren="已禁用" />
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
              配置步骤：
            </Text>
            <ol style={{ fontSize: 12, color: '#666', paddingLeft: 20, marginBottom: 16 }}>
              <li>在 Telegram 中搜索 @BotFather，发送 /newbot 创建机器人</li>
              <li>按提示设置机器人名称，获取 Bot Token</li>
              <li>获取 Chat ID：向你的 Bot 发送消息，然后访问 api.telegram.org/bot{'{token}'}/getUpdates</li>
              <li>将 Token 和 Chat ID 填入上方表单</li>
            </ol>

            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingTelegram}
              onClick={handleSaveTelegram}
            >
              保存 Telegram 设置
            </Button>
            <Button
              style={{ marginLeft: 12 }}
              onClick={handleTestTelegram}
            >
              发送测试消息
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'announcement',
      label: '公告设置',
      children: (
        <Card style={{ borderRadius: 8 }}>
          <Form form={announcementForm} layout="vertical">
            <Title level={5} style={{ marginBottom: 16 }}>
              网站公告
            </Title>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
              公告将显示在网站前台页面最顶部，用于展示促销信息、重要通知等。
            </Text>
            <Form.Item
              name="announcementEnabled"
              label="启用公告"
              valuePropName="checked"
            >
              <Switch checkedChildren="已启用" unCheckedChildren="已禁用" />
            </Form.Item>
            <Form.Item
              name="announcement"
              label="公告内容"
            >
              <TextArea
                rows={3}
                placeholder="请输入公告内容，如：新年促销活动进行中，全场商品 8 折优惠！"
                maxLength={200}
                showCount
              />
            </Form.Item>
            <Divider />
            <Title level={5} style={{ marginBottom: 16 }}>
              网站维护
            </Title>
            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
              开启后，前台页面会显示不可关闭的维护弹窗，用于暂停访问并展示维护说明。
            </Text>
            <Form.Item
              name="maintenanceEnabled"
              label="启用网站维护"
              valuePropName="checked"
            >
              <Switch checkedChildren="维护中" unCheckedChildren="正常访问" />
            </Form.Item>
            <Form.Item
              name="maintenanceMessage"
              label="维护提示语"
            >
              <TextArea
                rows={4}
                placeholder="请输入维护提示语，如：网站正在维护升级，预计稍后恢复访问。"
                maxLength={500}
                showCount
              />
            </Form.Item>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={savingAnnouncement}
              onClick={handleSaveAnnouncement}
            >
              保存公告设置
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: 'notifications',
      label: '通知模板',
      children: (
        <Card style={{ borderRadius: 8 }} bodyStyle={{ padding: 0 }}>
          <Table
            dataSource={emailTemplates}
            columns={templateColumns}
            rowKey="id"
            loading={loadingTemplates}
            pagination={false}
            locale={{ emptyText: '暂无邮件模板' }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ marginBottom: 16, marginTop: 0 }}>
        系统设置
      </Title>

      <Spin spinning={loadingSettings}>
        <Tabs
          defaultActiveKey="general"
          items={tabItems}
          type="card"
          style={{ background: 'transparent' }}
        />
      </Spin>

      {/* 邮件模板编辑 Modal */}
      <Modal
        title={`编辑邮件模板：${editingTemplate?.label || editingTemplate?.eventType || ''}`}
        open={templateModalVisible}
        onCancel={handleTemplateModalClose}
        onOk={handleSaveTemplate}
        okText="保存"
        cancelText="取消"
        confirmLoading={savingTemplate}
        width={720}
        destroyOnClose
      >
        <Form form={templateForm} layout="vertical" style={{ marginTop: 8 }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="label"
                label="模板名称"
                rules={[{ required: true, message: '请输入模板名称' }]}
              >
                <Input placeholder="如：订单确认通知" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="recipient"
                label="收件人类型"
                rules={[{ required: true, message: '请选择收件人类型' }]}
              >
                <Select>
                  <Option value="customer">客户</Option>
                  <Option value="admin">管理员</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="subject"
            label="邮件主题"
            rules={[{ required: true, message: '请输入邮件主题' }]}
          >
            <Input placeholder="邮件主题（支持变量，如 {{orderNo}}）" />
          </Form.Item>
          <Form.Item
            name="body"
            label="邮件正文"
            rules={[{ required: true, message: '请输入邮件正文' }]}
          >
            <RichTextEditor
              height={440}
              placeholder="邮件正文，支持富文本；可使用模板变量 {{customerName}}、{{orderNo}} 等"
            />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            提示：可使用模板变量，例如 {'{{customerName}}'}、{'{{orderNo}}'}、{'{{total}}'}
          </Text>
        </Form>
      </Modal>
    </div>
  );
}
